import { useEffect, useRef, type RefObject } from 'react'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

let locks = 0

/** What every modal surface (Dialog, Drawer, sheet) needs while open: focus
 *  moves in and stays in, Escape closes, the page behind stops scrolling, and
 *  focus returns to whatever opened it. */
export function useOverlay(
  open: boolean,
  onClose: () => void,
  panel: RefObject<HTMLElement | null>,
  /** `panel` focuses the surface itself: a drawer opens on something to
   *  read, and landing on its first link drew a focus ring on a channel
   *  name. Dialogs keep `first`, their first field. */
  initialFocus: 'first' | 'panel' = 'first',
) {
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    if (!open) return
    const before = document.activeElement as HTMLElement | null
    const node = panel.current
    const marked = node?.querySelector<HTMLElement>('[data-autofocus]')
    const first = marked ?? (initialFocus === 'first' ? node?.querySelector<HTMLElement>(FOCUSABLE) : null)
    ;(first ?? node)?.focus()

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        closeRef.current()
        return
      }
      if (e.key !== 'Tab' || !panel.current) return
      const items = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE))
      if (items.length === 0) {
        e.preventDefault()
        return
      }
      const firstEl = items[0]
      const lastEl = items[items.length - 1]
      const active = document.activeElement as HTMLElement | null
      if (e.shiftKey && (active === firstEl || active === panel.current || !panel.current.contains(active))) {
        e.preventDefault()
        lastEl.focus()
      } else if (!e.shiftKey && (active === lastEl || !panel.current.contains(active))) {
        e.preventDefault()
        firstEl.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    locks++
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      locks--
      if (locks === 0) document.body.style.overflow = ''
      before?.focus?.()
    }
  }, [open, panel])
}
